import { useCallback, useEffect, useState } from 'react';
import type {
  Entry,
  Friend,
  Group,
  Item,
  Person,
  PrayerNote,
  Sheet,
  SheetBrief,
} from '../domain.ts';
import { ApiError, isLive, request } from './client.ts';

/**
 * 친구 · 모임 · 기록지 데이터 소스 (화면 D1~D3 · F1~F4).
 *
 * notes.ts 와 다른 점이 하나 있습니다. **목업으로 떨어지지 않습니다.**
 * 노트는 토큰이 없어도 디자인을 확인할 수 있게 목업을 두었지만, 모임은
 * «누가 그룹장인가 · 누가 이 줄을 적을 수 있는가»가 서버가 정하는 값이라
 * 목업으로는 화면의 절반이 거짓이 됩니다. 토큰이 없으면 그렇다고 말합니다.
 */

/* ── 서버 표현 → 화면 표현 ───────────────────────────────────── */

/**
 * 노트는 서버가 noteId 를, 화면이 id 를 씁니다 (notes.ts 와 같은 변환).
 *
 * 여기 또 두는 것이 아깝지만, notes.ts 의 것은 그 파일 안에 숨어 있고
 * 꺼내 쓰자면 순환 참조가 생깁니다 — 기록지가 노트를 담고, 노트 화면이
 * 기록지를 열지는 않으므로 방향이 한쪽인 지금이 더 단순합니다.
 */
interface ServerNote {
  noteId: string;
  authorId: string;
  progress?: PrayerNote['progress'];
  history?: PrayerNote['history'];
  [key: string]: unknown;
}

function toNote(raw: ServerNote): PrayerNote {
  return {
    ...(raw as unknown as PrayerNote),
    id: raw.noteId,
    author: raw.authorId,
    progress: raw.progress ?? [],
    history: raw.history ?? [],
  };
}

interface ServerEntry extends Omit<Entry, 'notes'> {
  notes: ServerNote[];
}

interface ServerItem extends Omit<Item, 'entries'> {
  entries: ServerEntry[];
}

interface ServerSheet extends Omit<Sheet, 'items'> {
  items: ServerItem[];
}

interface ServerFriend extends Omit<Friend, 'representative'> {
  representative?: ServerNote[];
}

function toEntry(raw: ServerEntry): Entry {
  return { ...raw, notes: raw.notes.map(toNote) };
}

function toSheet(raw: ServerSheet): Sheet {
  return {
    ...raw,
    items: raw.items.map((item) => ({ ...item, entries: item.entries.map(toEntry) })),
  };
}

function toFriend(raw: ServerFriend): Friend {
  // 스프레드만 쓰면 서버 모양의 representative 가 그대로 남습니다.
  // 키를 하나씩 옮겨 옛 모양이 화면으로 새지 않게 합니다
  const { representative, ...rest } = raw;
  return {
    ...rest,
    ...(representative ? { representative: representative.map(toNote) } : {}),
  };
}

/* ── 한 번 불러오고 다시 불러오기 ────────────────────────────── */

export interface Loaded<T> {
  data: T | null;
  loading: boolean;
  /** 토큰이 없어 부를 수 없는 상태. 화면이 「준비 중」 대신 이유를 말합니다 */
  offline: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * 경로 하나를 읽어 오는 훅.
 *
 * `path` 가 null 이면 아직 부를 때가 아니라는 뜻입니다 — 모임을 고르기 전의
 * 기록지 목록처럼, 열리기 전에는 무엇을 부를지 정해지지 않은 화면이 있습니다.
 */
function useLoaded<T>(
  path: string | null,
  pick: (raw: never) => T,
  fallback: string,
): Loaded<T> {
  const live = isLive();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(live && path !== null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!live || path === null) {
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    setError(null);

    request<never>(path)
      .then((res) => {
        if (alive) setData(pick(res));
      })
      .catch((err: unknown) => {
        // 실패했을 때 앞서 읽은 것을 남겨 둡니다 — 새로 고치다 실패했다고
        // 화면이 통째로 비면 무엇을 보고 있었는지 잃어버립니다
        if (alive) setError(err instanceof ApiError ? err.message : fallback);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
    // pick 은 호출부에서 새로 만들어지므로 의존성에 넣지 않습니다 —
    // 넣으면 매 렌더마다 다시 불러옵니다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, path, tick]);

  return {
    data,
    loading,
    offline: !live,
    error,
    reload: useCallback(() => setTick((t) => t + 1), []),
  };
}

/* ── 친구 ─────────────────────────────────────────────────────── */

export function useFriends(): Loaded<Friend[]> {
  return useLoaded<Friend[]>(
    '/friends',
    (res: { friends: ServerFriend[] }) => res.friends.map(toFriend),
    '친구 목록을 불러오지 못했습니다.',
  );
}

/** D2 · D2-1 — 팝업을 열 때만 부릅니다. 대표 기도제목이 여기 담겨 옵니다 */
export function useFriend(userId: string | null): Loaded<Friend> {
  return useLoaded<Friend>(
    userId === null ? null : `/friends/${userId}`,
    (res: { friend: ServerFriend }) => toFriend(res.friend),
    '친구 정보를 불러오지 못했습니다.',
  );
}

export async function addFriend(userId: string): Promise<Friend> {
  const res = await request<{ friend: ServerFriend }>('/friends', {
    method: 'POST',
    body: { userId },
  });
  return toFriend(res.friend);
}

export async function patchFriend(
  userId: string,
  changes: {
    pinned?: boolean;
    state?: Friend['state'];
    /**
     * 차단할 때 이미 가 있는 노트를 회수할지 — screens.md 7절.
     * 반응(이모티콘 · 코멘트)은 이 값과 **무관하게 전부 사라집니다.**
     */
    revokeShared?: boolean;
  },
): Promise<Friend> {
  const res = await request<{ friend: ServerFriend }>(`/friends/${userId}`, {
    method: 'PATCH',
    body: changes,
  });
  return toFriend(res.friend);
}

export async function removeFriend(userId: string): Promise<void> {
  await request<void>(`/friends/${userId}`, { method: 'DELETE' });
}

/* ── 초대 코드 — D1-1 ─────────────────────────────────────────── */

export interface Invite {
  /** 저장된 모양. 서버에 보낼 때 쓰지는 않습니다 — 사람이 적어 넣은 것을 보냅니다 */
  code: string;
  /** 네 글자씩 끊은 보여 주는 모양 — ACDE-FGHJ */
  pretty: string;
}

/**
 * 내 초대 코드.
 *
 * 없으면 서버가 그때 만들어 주므로 «읽기»만은 아니지만, 부르는 쪽에서 보면
 * 두 번 불러도 같은 값이라 GET 입니다.
 */
export function useInviteCode(enabled: boolean): Loaded<Invite> {
  return useLoaded<Invite>(
    enabled ? '/friends/code' : null,
    (res: { invite: Invite }) => res.invite,
    '초대 코드를 불러오지 못했습니다.',
  );
}

/** 새로 뽑습니다. **옛 코드는 그 순간 죽습니다** — 화면이 먼저 알립니다 */
export async function reissueInviteCode(): Promise<Invite> {
  const res = await request<{ invite: Invite }>('/friends/code', { method: 'POST' });
  return res.invite;
}

/** 받은 코드로 친구 추가. 하이픈이나 공백이 섞여 있어도 서버가 걸러 냅니다 */
export async function addFriendByCode(code: string): Promise<Friend> {
  const res = await request<{ friend: ServerFriend }>('/friends/by-code', {
    method: 'POST',
    body: { code },
  });
  return toFriend(res.friend);
}

/** F2 · F3-2-1 에서 고를 수 있는 사람 — 차단한 상대는 빠져서 옵니다 */
export function usePickableFriends(): Loaded<Person[]> {
  return useLoaded<Person[]>(
    '/friends/pickable',
    (res: { people: Person[] }) => res.people,
    '친구 목록을 불러오지 못했습니다.',
  );
}

/* ── 모임 ─────────────────────────────────────────────────────── */

export function useGroups(): Loaded<Group[]> {
  return useLoaded<Group[]>(
    '/groups',
    (res: { groups: Group[] }) => res.groups,
    '모임을 불러오지 못했습니다.',
  );
}

export function useGroup(groupId: string | null): Loaded<Group> {
  return useLoaded<Group>(
    groupId === null ? null : `/groups/${groupId}`,
    (res: { group: Group }) => res.group,
    '모임을 불러오지 못했습니다.',
  );
}

export interface NewGroup {
  name: string;
  intro: string;
  photo?: string;
  members: string[];
}

export async function createGroup(input: NewGroup): Promise<Group> {
  const res = await request<{ group: Group }>('/groups', { method: 'POST', body: input });
  return res.group;
}

export interface GroupPatch {
  name?: string;
  intro?: string;
  photo?: string;
  /** 사진을 떼기 — photo 만으로는 «안 보냄»과 구분이 안 됩니다 */
  clearPhoto?: boolean;
  members?: string[];
  ownerId?: string;
}

export async function patchGroup(groupId: string, changes: GroupPatch): Promise<Group> {
  const res = await request<{ group: Group }>(`/groups/${groupId}`, {
    method: 'PATCH',
    body: changes,
  });
  return res.group;
}

/**
 * 모임 나가기 — F3-1-2 · F3-1-3.
 *
 * 그룹장이면 `nextOwner` 가 있어야 합니다. 없으면 서버가 거절하고, 화면에서도
 * 고르기 전에는 「넘기고 나가기」가 눌리지 않습니다.
 */
export async function leaveGroup(groupId: string, nextOwner?: string): Promise<void> {
  await request<void>(`/groups/${groupId}/leave`, {
    method: 'POST',
    body: nextOwner === undefined ? {} : { ownerId: nextOwner },
  });
}

/* ── 기록지 ───────────────────────────────────────────────────── */

export function useSheets(groupId: string | null): Loaded<SheetBrief[]> {
  return useLoaded<SheetBrief[]>(
    groupId === null ? null : `/groups/${groupId}/sheets`,
    (res: { sheets: SheetBrief[] }) => res.sheets,
    '기록지를 불러오지 못했습니다.',
  );
}

export function useSheet(sheetId: string | null): Loaded<Sheet> {
  return useLoaded<Sheet>(
    sheetId === null ? null : `/sheets/${sheetId}`,
    (res: { sheet: ServerSheet }) => toSheet(res.sheet),
    '기록지를 불러오지 못했습니다.',
  );
}

export interface NewSheet {
  title: string;
  metOn: string;
  participants: string[];
}

export async function createSheet(groupId: string, input: NewSheet): Promise<Sheet> {
  const res = await request<{ sheet: ServerSheet }>(`/groups/${groupId}/sheets`, {
    method: 'POST',
    body: input,
  });
  return toSheet(res.sheet);
}

export async function patchSheet(
  sheetId: string,
  changes: { title?: string; metOn?: string; participants?: string[] },
): Promise<Sheet> {
  const res = await request<{ sheet: ServerSheet }>(`/sheets/${sheetId}`, {
    method: 'PATCH',
    body: changes,
  });
  return toSheet(res.sheet);
}

export async function deleteSheet(sheetId: string): Promise<void> {
  await request<void>(`/sheets/${sheetId}`, { method: 'DELETE' });
}

/**
 * 항목 추가 — F4-5.
 *
 * 응답이 항목 하나가 아니라 기록지 전체인 것은, 항목이 늘면 순서가 바뀌고
 * 결단은 줄까지 생기기 때문입니다.
 */
export async function addItem(sheetId: string, kind: Item['kind']): Promise<Sheet> {
  const res = await request<{ sheet: ServerSheet }>(`/sheets/${sheetId}/items`, {
    method: 'POST',
    body: { kind },
  });
  return toSheet(res.sheet);
}

export async function patchItem(
  itemId: string,
  changes: { verse?: string; interpretation?: string; questions?: string; sortOrder?: number },
): Promise<Sheet> {
  const res = await request<{ sheet: ServerSheet }>(`/items/${itemId}`, {
    method: 'PATCH',
    body: changes,
  });
  return toSheet(res.sheet);
}

export async function deleteItem(itemId: string): Promise<Sheet> {
  const res = await request<{ sheet: ServerSheet }>(`/items/${itemId}`, { method: 'DELETE' });
  return toSheet(res.sheet);
}

/* ── 줄 ───────────────────────────────────────────────────────── */

/** F4-2 — 직접 적기면 title·body, 대표 공유면 noteIds 를 보냅니다 */
export async function patchEntry(
  entryId: string,
  changes: { mode: Entry['mode']; title?: string; body?: string; noteIds?: string[] },
): Promise<Sheet> {
  const res = await request<{ sheet: ServerSheet }>(`/entries/${entryId}`, {
    method: 'PATCH',
    body: changes,
  });
  return toSheet(res.sheet);
}

/** F4-2 오른쪽에서 고를 목록 — 그 사람의 대표 기도제목 */
export async function shareableNotes(entryId: string): Promise<PrayerNote[]> {
  const res = await request<{ notes: ServerNote[] }>(`/entries/${entryId}/shareable`);
  return res.notes.map(toNote);
}

/** F4-2-1 — 내 Prayer Book 으로 저장 */
export async function saveEntryToBook(
  entryId: string,
  input: { category: string; plan: PrayerNote['plan']; isPrivate: boolean },
): Promise<PrayerNote> {
  const res = await request<{ note: ServerNote }>(`/entries/${entryId}/note`, {
    method: 'POST',
    body: input,
  });
  return toNote(res.note);
}
